/**
 * Share Service - Frontend Service
 * Handles sharing of AI responses via share links
 */

// 1.5.15: Uses shared/api client for BFF/legacy routing
import { logger } from '../utils/logger';
import { getCurrentSession, getCurrentUser } from './authService';
import { getAnonymousUserId } from '../lib/supabase';
import type { CreateShareRequest, CreateShareResponse, SharedContent } from '../../features/share/types/share';
import { resolveUrl, buildHeaders } from '../api/client';

/**
 * Create a share link for an AI response
 * @param request Share request containing message content and associated data
 * @returns Share response with share_token and share_url
 */
export async function createShare(
  request: CreateShareRequest
): Promise<CreateShareResponse> {
  const startTime = Date.now();

  logger.log('📤 Creating share link:', {
    message_id: request.message_id,
    query_text: request.query_text.substring(0, 50) + '...',
    has_restaurants: !!request.restaurants && request.restaurants.length > 0,
  });

  try {
    // Get current user session to obtain access token for authenticated users
    const session = await getCurrentSession();
    const authToken = session?.access_token || SUPABASE_ANON_KEY;

    // Get user ID (authenticated user UUID or anonymous user ID)
    // This ensures we always record a user ID, even for anonymous users
    let userId: string | undefined;
    try {
      const user = await getCurrentUser();
      if (user) {
        // Authenticated user: use UUID from auth.users
        userId = user.id;
      } else {
        // Anonymous user: use anonymous user ID from localStorage
        userId = getAnonymousUserId();
      }
    } catch (error) {
      // Fallback to anonymous user ID if there's any error
      userId = getAnonymousUserId();
    }

    // Add user_id to request body
    const requestWithUserId: CreateShareRequest = {
      ...request,
      user_id: userId,
    };

    const url = resolveUrl('share-service', '/create');
    const headers = buildHeaders({ authToken });

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestWithUserId),
    });

    if (!response.ok) {
      let errorData: any = {};
      try {
        const text = await response.text();
        errorData = text ? JSON.parse(text) : {};
      } catch (e) {
        // If parsing fails, use empty object
        logger.warn('Failed to parse error response:', e);
      }
      logger.error('❌ API Error - Create share failed:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
        url,
      });
      throw new Error(
        errorData.error || errorData.message || `Create share failed: ${response.status} ${response.statusText}`
      );
    }

    const data: CreateShareResponse = await response.json();

    logger.log('✅ Share link created successfully:', {
      share_token: data.share_token,
      share_url: data.share_url,
      isAuthenticated: !!session,
      userId: userId,
      processingTime: `${Date.now() - startTime}ms`,
    });

    return data;
  } catch (error: any) {
    logger.error('❌ API Error - Create share failed:', {
      error: error.message,
      errorName: error.name,
      errorStack: error.stack,
      url: resolveUrl('share-service', '/create'),
      processingTime: `${Date.now() - startTime}ms`,
    });
    
    // Provide more helpful error messages
    if (error.message.includes('Failed to fetch') || error.message.includes('ERR_FAILED')) {
      throw new Error('无法连接到服务器，请检查网络连接或确认 Edge Function 已部署');
    }
    throw new Error(`API Error - Create share failed: ${error.message}`);
  }
}

/**
 * Get shared content by share token
 * This will automatically increment the view_count in the database
 * @param shareToken Share token from the share URL
 * @returns Shared content including message, query, and restaurants
 */
export async function getSharedContent(
  shareToken: string
): Promise<SharedContent> {
  const startTime = Date.now();

  logger.log('📥 Fetching shared content:', {
    share_token: shareToken,
  });

  try {
    const url = resolveUrl('share-service', `/${shareToken}`);
    const headers = buildHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      let errorData: any = {};
      try {
        const text = await response.text();
        errorData = text ? JSON.parse(text) : {};
      } catch (e) {
        // If parsing fails, use empty object
        logger.warn('Failed to parse error response:', e);
      }
      logger.error('❌ API Error - Get share failed:', {
        status: response.status,
        statusText: response.statusText,
        errorData,
        url,
      });
      throw new Error(
        errorData.error || errorData.message || `Get share failed: ${response.status} ${response.statusText}`
      );
    }

    const data: SharedContent = await response.json();

    logger.log('✅ Shared content retrieved successfully:', {
      share_token: shareToken,
      view_count: data.view_count,
      has_restaurants: !!data.restaurants && data.restaurants.length > 0,
      processingTime: `${Date.now() - startTime}ms`,
    });

    return data;
  } catch (error: any) {
    logger.error('❌ API Error - Get share failed:', {
      error: error.message,
      errorName: error.name,
      errorStack: error.stack,
      url: resolveUrl('share-service', `/${shareToken}`),
      processingTime: `${Date.now() - startTime}ms`,
    });
    
    // Provide more helpful error messages
    if (error.message.includes('Failed to fetch') || error.message.includes('ERR_FAILED')) {
      throw new Error('无法连接到服务器，请检查网络连接或确认 Edge Function 已部署');
    }
    throw new Error(`API Error - Get share failed: ${error.message}`);
  }
}

